import { MINUTE, toIso, toMs } from '../../domain/time';
import { newId, type Id, type Payment } from '../../domain/types';
import type { Clock, PaymentsAdapter, StorageAdapter, Unsubscribe } from '../types';

/** Mock gateway: payments live in storage; the checkout page is `#/demo/pay/:id` (Success / Fail / Timeout). */
export function createMockPayments(deps: { storage: StorageAdapter; clock: Clock }): PaymentsAdapter {
  const { storage, clock } = deps;
  const repo = storage.repo('payments');
  const listeners = new Set<(p: Payment) => void>();
  const announce = (p: Payment): void => { for (const cb of [...listeners]) cb(p); };

  async function transition(id: Id, change: Partial<Payment>, from: Payment['status'][]): Promise<Payment | undefined> {
    const cur = await repo.get(id);
    if (!cur || !from.includes(cur.status)) return cur;
    const next: Payment = { ...cur, ...change, updatedAt: clock.nowIso() };
    await repo.put(next);
    announce(next);
    return next;
  }

  return {
    label: 'Payment gateway (demo, no real charge)',
    async createDeposit(req) {
      const now = clock.nowIso();
      const p: Payment = {
        id: newId(clock.now()), businessId: req.businessId, createdAt: now, updatedAt: now,
        appointmentId: req.appointmentId, amountAgorot: req.amountAgorot, refundedAgorot: 0,
        status: 'pending', gateway: 'mock', expiresAt: toIso(clock.now() + req.holdMinutes * MINUTE),
      };
      await repo.put(p);
      return { paymentId: p.id, checkoutUrl: `#/demo/pay/${p.id}` };
    },
    getStatus: (id) => repo.get(id),
    async refund(id, amount) {
      const cur = await repo.get(id);
      if (!cur) return { ok: false, reason: 'not_found' };
      if (cur.status !== 'paid' && cur.status !== 'partially_refunded') return { ok: false, reason: 'not_paid' };
      const refundable = cur.amountAgorot - cur.refundedAgorot;
      const value = amount ?? refundable;
      if (value > refundable || value <= 0) return { ok: false, reason: 'exceeds_paid' };
      const refunded = cur.refundedAgorot + value;
      const next: Payment = { ...cur, refundedAgorot: refunded, status: refunded >= cur.amountAgorot ? 'refunded' : 'partially_refunded', updatedAt: clock.nowIso() };
      await repo.put(next);
      announce(next);
      return { ok: true, payment: next };
    },
    async complete(id, outcome) {
      if (outcome === 'success') {
        const cur = await repo.get(id);
        if (cur && cur.status === 'pending' && toMs(cur.expiresAt) <= clock.now()) {
          return transition(id, { status: 'expired' }, ['pending']);
        }
        return transition(id, { status: 'paid', paidAt: clock.nowIso() }, ['pending']);
      }
      return transition(id, { status: outcome === 'failure' ? 'failed' : 'expired' }, ['pending']);
    },
    async sweepExpired() {
      const now = clock.now();
      const due = await repo.list({ where: { status: 'pending' }, filter: (p) => toMs(p.expiresAt) <= now });
      const out: Payment[] = [];
      for (const p of due) {
        const next = await transition(p.id, { status: 'expired' }, ['pending']);
        if (next) out.push(next);
      }
      return out;
    },
    onStatus(cb): Unsubscribe { listeners.add(cb); return () => void listeners.delete(cb); },
  };
}
